import {
  apiAttach_new_document,
  apiGetAccountType,
  apiGetUsername,
  apiUpdate_user_id_address,
  apiDoId3,
  apiVerificationV2,
} from '@/resource'
import {
  apiGetCurrentStepV2,
  apiGetDataV2,
  apiProcessV2,
  apiCountries,
  apiProvinces,
  apiNationalities,
  apiPhoneCodes,
  apiEmploymentFinance,
  apiTrading,
} from '@/resource/register'

export default Object.freeze({
  async getCurrentStep() {
    try {
      const response = await apiGetCurrentStepV2()
      if (response.data.code === 0 && response.data?.data) {
        return response.data.data
      }
      throw new Error(response.data?.errmsg ?? '')
    } catch (error) {
      if (error?.response?.data?.errmsg?.length > 0) {
        return new Error(error.response.data.errmsg)
      }
      return error
    }
  },
  async getUsername() {
    try {
      const response = await apiGetUsername()
      if (response.data.code === 0 && response.data?.data) {
        return response.data.data
      }
      throw new Error(response.data?.errmsg ?? '')
    } catch (error) {
      if (error?.response?.data?.errmsg?.length > 0) {
        return new Error(error.response.data.errmsg)
      }
      return error
    }
  },
  async getData(step) {
    try {
      const response = await apiGetDataV2(step)
      if (response.data.code === 0 && response.data?.data) {
        return response.data.data
      }
      throw new Error(response.data?.errmsg ?? '')
    } catch (error) {
      if (error?.response?.data?.errmsg?.length > 0) {
        return new Error(error.response.data.errmsg)
      }
      return error
    }
  },
  async process({ step, form }) {
    try {
      const response = await apiProcessV2({ step, ...form })
      if (response.data.code === 0 && response.data?.data) {
        return response.data.data
      }
      throw new Error(response.data?.errmsg ?? '')
    } catch (error) {
      if (error?.response?.data?.errmsg?.length > 0) {
        return new Error(error.response.data.errmsg)
      }
      return error
    }
  },
  // Boolean || Error
  // For resubmit id/poa
  async sendVerification({
    filePathList = [],
    // enum
    fileType,
    // required if !isPending
    auditType = null,
    // required if isPending
    fileId = null,
    isPending = false,
    prevExistingFilePathList = [],
  }) {
    try {
      // Validation
      if (isPending) {
        if (fileId === null) {
          throw new Error('fileId required for isPending true')
        }
      } else {
        if (auditType === null) {
          throw new Error('auditType required for isPending false')
        }
      }

      let response = null
      if (isPending) {
        // need to addon existing file here as request from BE
        // existing with file path, new with aws link
        const mergedList = [...prevExistingFilePathList, ...filePathList]

        response = await apiAttach_new_document({
          documentId: fileId,
          documentType: fileType,
          docPaths: mergedList,
        })
      } else {
        response = await apiVerificationV2({ filePathList, fileType, auditType })
      }

      if (response.data.code === 0 && response.data?.data === 'SUCCESS') {
        return true
      }
      throw new Error(response.data?.errmsg ?? '')
    } catch (error) {
      if (error?.response?.data?.errmsg?.length > 0) {
        return new Error(error.response.data.errmsg)
      }
      return error
    }
  },
  async updateUserIdAddress({
    idType = null,
    idNumber = null,
    firstName = null,
    lastName = null,
    nationalityId = null,
    address = null,
    streetNumber = null,
    suburb = null,
    state = null,
    postcode = null,
  }) {
    try {
      const response = await apiUpdate_user_id_address({
        idType,
        idNumber,
        firstName,
        lastName,
        nationalityId,
        address,
        streetNumber,
        suburb,
        state,
        postcode,
      })
      if (response.data.code === 0 && response.data?.data === 'SUCCESS') {
        return true
      }
      throw new Error(response.data?.errmsg ?? '')
    } catch (error) {
      if (error?.response?.data?.errmsg?.length > 0) {
        return new Error(error.response.data.errmsg)
      }
      return error
    }
  },
  async doId3({ address = null, streetNumber = null, suburb = null, state = null, postcode = null }) {
    try {
      const response = await apiDoId3({
        address,
        streetNumber,
        suburb,
        state,
        postcode,
      })
      if (response.data.code === 0 && response.data?.data !== null) {
        return response.data.data
      }
      throw new Error(response.data?.errmsg ?? '')
    } catch (error) {
      if (error?.response?.data?.errmsg?.length > 0) {
        return new Error(error.response.data.errmsg)
      }
      return error
    }
  },
  // Personal Information
  async getNationalities(lang = 'en_US') {
    try {
      const response = await apiNationalities(lang)
      if (response.data.code === 0 && response.data?.data) {
        return response.data.data
      }
      throw new Error(response.data?.errmsg ?? '')
    } catch (error) {
      if (error?.response?.data?.errmsg?.length > 0) {
        return new Error(error.response.data.errmsg)
      }
      return error
    }
  },
  async getPhoneCodes() {
    try {
      const response = await apiPhoneCodes()
      if (response.data.code === 0 && response.data?.data) {
        return response.data.data
      }
      throw new Error(response.data?.errmsg ?? '')
    } catch (error) {
      if (error?.response?.data?.errmsg?.length > 0) {
        return new Error(error.response.data.errmsg)
      }
      return error
    }
  },
  // Address
  async getCountries(lang = 'en_US') {
    try {
      const response = await apiCountries(lang)
      if (response.data.code === 0 && response.data?.data) {
        return response.data.data
      }
      throw new Error(response.data?.errmsg ?? '')
    } catch (error) {
      if (error?.response?.data?.errmsg?.length > 0) {
        return new Error(error.response.data.errmsg)
      }
      return error
    }
  },
  // return AUSTRALIA provinces if countryId is null (null in this case)
  async getProvinces(lang = 'en_US') {
    try {
      const response = await apiProvinces(lang)
      if (response.data.code === 0 && response.data?.data) {
        return response.data.data
      }
      throw new Error(response.data?.errmsg ?? '')
    } catch (error) {
      if (error?.response?.data?.errmsg?.length > 0) {
        return new Error(error.response.data.errmsg)
      }
      return error
    }
  },

  // Account Configuration
  async getAccountType() {
    try {
      const response = await apiGetAccountType()
      if (response.data.code === 0 && response.data?.data) {
        return response.data.data
      }
      throw new Error(response.data?.errmsg ?? '')
    } catch (error) {
      if (error?.response?.data?.errmsg?.length > 0) {
        return new Error(error.response.data.errmsg)
      }
      return error
    }
  },

  // Employment Financial Question
  async getEmploymentFinanceQuestions(lang = 'en_US') {
    try {
      const response = await apiEmploymentFinance(lang)
      if (response.data.code === 0 && response.data?.data) {
        return response.data.data
      }
      throw new Error(response.data?.errmsg ?? '')
    } catch (error) {
      if (error?.response?.data?.errmsg?.length > 0) {
        return new Error(error.response.data.errmsg)
      }
      return error
    }
  },
  async getTradingQuestions(lang = 'en_US') {
    try {
      const response = await apiTrading(lang)
      if (response.data.code === 0 && response.data?.data) {
        return response.data.data
      }
      throw new Error(response.data?.errmsg ?? '')
    } catch (error) {
      if (error?.response?.data?.errmsg?.length > 0) {
        return new Error(error.response.data.errmsg)
      }
      return error
    }
  },
})
